/* design-rules: allow-hex — beyaz etiket müşteri marka renkleri ve WCAG hesabı: tema token'ı değil müşteri verisi.
   `on-brand` (#111111) ve yüzey değerleri tokens.json ile aynıdır; domain paketi istemcisiz kalsın diye burada sabit. */
// Beyaz etiket (C84): erişilebilir hazır marka renkleri, kontrast denetimi ve tek renkten marka ailesi.

/** Marka rengi biçimi uyarısı (örnek SEROTA turuncusu). */
export const HEX_COLOR_MESSAGE = 'Rengi #RRGGBB biçiminde girin (ör. #FF5B2E).';

/** `on-brand` token'ı: turuncu dolgu üzerinde koyu metin (tasarım sistemi kuralı). */
export const ON_BRAND_DARK = '#111111';
/** Metin kontrastı alt sınırı (WCAG AA). */
export const MIN_TEXT_CONTRAST = 4.5;
/** Erişilebilir hazır renkler: hepsi koyu metinle 4.5:1 üstü ve durum renklerinden (amber, kırmızı, yeşil, mavi) ayrık. */
export const BRAND_PRESETS: { name: string; hex: string }[] = [
  { name: 'SEROTA turuncu', hex: '#FF5B2E' },
  { name: 'Mercan', hex: '#FF6F61' },
  { name: 'Şeftali', hex: '#FF9F6E' },
  { name: 'Pembe', hex: '#F472B6' },
  { name: 'Orkide', hex: '#E879F9' },
  { name: 'Lavanta', hex: '#A78BFA' },
  { name: 'Turkuaz', hex: '#2EC4B6' },
];

export const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;

function channel(v: number): number {
  const s = v / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

function rgb(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.replace('#', '').slice(0, 6), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function hex([r, g, b]: [number, number, number]): string {
  return '#' + [r, g, b].map((c) => Math.round(c).toString(16).padStart(2, '0').toUpperCase()).join('');
}

/** WCAG 2.x göreli parlaklık ve kontrast oranı (@serota/tokens/contrast ile aynı formül). */
export function wcagContrast(a: string, b: string): number {
  const lum = (h: string) => {
    const [r, g, bl] = rgb(h);
    return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(bl);
  };
  const la = lum(a);
  const lb = lum(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** "4,5:1" biçimi. */
export function formatRatio(r: number): string {
  return `${(Math.floor(r * 10) / 10).toFixed(1).replace('.', ',')}:1`;
}

/** Marka rengi kontrol: koyu `on-brand` metni ≥4.5:1 değilse Türkçe hata metni döner. */
export function brandContrastError(brand: string): string | null {
  if (!HEX_COLOR.test(brand)) return HEX_COLOR_MESSAGE;
  const r = wcagContrast(brand, ON_BRAND_DARK);
  if (r >= MIN_TEXT_CONTRAST) return null;
  return `Bu renk üzerinde koyu yazı yeterince okunmuyor (${formatRatio(r)}). En az 4,5:1 gerekir; daha açık bir ton seçin.`;
}

export interface WhiteLabelPalette {
  brand: string;
  brandStrong: string;
  brandInk: string;
  brandTint: string;
  onBrand: string;
}

/**
 * Tek marka renginden aile türetir (01-product-architecture.md "Beyaz etiket"). `on-brand` her zaman koyu kalır;
 * `brand-strong` koyu metinle 4.5:1'i korur, `brand-ink` yüzey ve tint üstünde 4.5:1 olana kadar koyulaşır (koyu
 * temada açılır). Yüzey renkleri token değerleridir; konsol `@serota/tokens`'tan geçirir.
 */
export function whiteLabelPalette(
  brand: string,
  theme: 'light' | 'dark' = 'light',
  surface = theme === 'dark' ? '#171717' : '#FFFFFF',
): WhiteLabelPalette {
  const base = rgb(brand);
  const target = rgb(surface);
  const mix = (t: number, to: [number, number, number]) =>
    hex(base.map((c, i) => c + (to[i]! - c) * t) as [number, number, number]);
  const black: [number, number, number] = [0, 0, 0];
  const white: [number, number, number] = [255, 255, 255];
  let strong = mix(theme === 'dark' ? 0.15 : 0.1, theme === 'dark' ? white : black);
  for (let t = 0.1; wcagContrast(strong, ON_BRAND_DARK) < MIN_TEXT_CONTRAST && t > 0; t -= 0.02) strong = mix(t, black);
  if (wcagContrast(strong, ON_BRAND_DARK) < MIN_TEXT_CONTRAST) strong = brand.toUpperCase();
  const tint = mix(theme === 'dark' ? 0.78 : 0.88, target);
  let ink = brand.toUpperCase();
  for (
    let t = 0.1;
    (wcagContrast(ink, surface) < MIN_TEXT_CONTRAST || wcagContrast(ink, tint) < MIN_TEXT_CONTRAST) && t <= 0.95;
    t += 0.05
  )
    ink = mix(t, theme === 'dark' ? white : black);
  return { brand: brand.toUpperCase(), brandStrong: strong, brandInk: ink, brandTint: tint, onBrand: ON_BRAND_DARK };
}
